import React, { useState, useEffect, useCallback } from 'react';
import { Network, Send, Inbox, Users, MapPin, Award, ArrowRight, Check, X, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

export default function LeadNetwork() {
  const [activeView, setActiveView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [zipFilter, setZipFilter] = useState('');
  const [showReferDialog, setShowReferDialog] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', interest: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get('/api/lead-network/stats'); setStats(data); } catch {}
  }, []);

  const fetchDealers = useCallback(async () => {
    try {
      const params = zipFilter ? { zip_prefix: zipFilter } : {};
      const { data } = await api.get('/api/lead-network/dealers', { params });
      setDealers(data);
    } catch {}
  }, [zipFilter]);

  const fetchReferrals = useCallback(async (dir) => {
    try { const { data } = await api.get(`/api/lead-network/referrals?direction=${dir}`); setReferrals(data); } catch {}
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeView === 'dealers') fetchDealers(); }, [activeView, fetchDealers]);
  useEffect(() => {
    if (activeView === 'sent') fetchReferrals('sent');
    if (activeView === 'received') fetchReferrals('received');
  }, [activeView, fetchReferrals]);

  const handleRefer = async () => {
    if (!leadForm.name) { toast.error('Lead name is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/lead-network/referral', {
        target_dealer_id: selectedDealer.id,
        lead_info: leadForm
      });
      toast.success(`Lead referred to ${selectedDealer.name}`);
      setShowReferDialog(false);
      setLeadForm({ name: '', email: '', phone: '', interest: '' });
      setSelectedDealer(null);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send referral');
    } finally { setSubmitting(false); }
  };

  const handleRespond = async (referralId, action) => {
    try {
      await api.post(`/api/lead-network/referrals/${referralId}/respond`, { action });
      toast.success(action === 'accept' ? 'Referral accepted & added to CRM!' : 'Referral declined');
      fetchReferrals('received');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    }
  };

  const views = [
    { id: 'overview', icon: Network, label: 'Overview' },
    { id: 'dealers', icon: Users, label: 'Find Dealers' },
    { id: 'sent', icon: Send, label: 'Sent' },
    { id: 'received', icon: Inbox, label: 'Received' },
  ];

  return (
    <div className="space-y-6" data-testid="lead-network">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-rose-500 to-orange-500 flex items-center justify-center shadow-lg">
          <Network className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-xl">Lead Generation Network</h3>
          <p className="text-gray-500 text-sm">Refer leads, find partners, grow together</p>
        </div>
      </div>

      {/* Sub-nav */}
      <div className="flex gap-2 bg-gray-50 p-1 rounded-xl" data-testid="lead-network-nav">
        {views.map(v => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeView === v.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`network-tab-${v.id}`}
          >
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeView === 'overview' && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Sent', value: stats.sent, sub: `${stats.sent_accepted} accepted` },
            { label: 'Received', value: stats.received, sub: `${stats.received_pending} pending` },
            { label: 'Acceptance Rate', value: `${stats.sent_acceptance_rate}%`, sub: 'sent referrals' },
            { label: 'Network Score', value: stats.network_score, sub: 'out of 100' },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5" data-testid={`network-stat-${i}`}>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-sm font-medium text-gray-600">{s.label}</p>
              <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Find Dealers */}
      {activeView === 'dealers' && (
        <div className="space-y-4">
          <div className="flex gap-3">
            <Input
              value={zipFilter}
              onChange={e => setZipFilter(e.target.value)}
              placeholder="Filter by ZIP prefix..."
              className="w-48 rounded-xl"
              data-testid="dealer-zip-filter"
            />
            <Button onClick={fetchDealers} variant="outline" className="rounded-xl">Search</Button>
          </div>
          {dealers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No dealers found{zipFilter ? ' for this area' : ''}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dealers.map(d => (
                <div key={d.id} className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all" data-testid={`dealer-card-${d.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                      {d.name?.[0] || 'D'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{d.name}</p>
                      <p className="text-xs text-gray-500 truncate">{d.company_name || 'Independent Dealer'}</p>
                    </div>
                  </div>
                  {d.location && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      <span>{d.location.city}, {d.location.state} {d.location.zip_code}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                    <Award className="w-3 h-3" />
                    <span>{d.successful_referrals} successful referrals</span>
                  </div>
                  <Button
                    size="sm"
                    className="w-full rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 text-white hover:opacity-90"
                    onClick={() => { setSelectedDealer(d); setShowReferDialog(true); }}
                    data-testid={`refer-btn-${d.id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Refer a Lead
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sent/Received Referrals */}
      {(activeView === 'sent' || activeView === 'received') && (
        <div className="space-y-3">
          {referrals.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Send className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No {activeView} referrals yet</p>
            </div>
          ) : referrals.map(r => (
            <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-4" data-testid={`referral-${r.id}`}>
              <div className={`w-2 h-10 rounded-full ${r.status === 'accepted' ? 'bg-emerald-500' : r.status === 'declined' ? 'bg-red-400' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{r.lead_info?.name || 'Unknown'}</p>
                <p className="text-xs text-gray-500">
                  {activeView === 'sent' ? `To: ${r.target_dealer_name}` : `From: ${r.referrer_name} (${r.referrer_company || 'Independent'})`}
                </p>
                {r.lead_info?.interest && <p className="text-xs text-gray-400 mt-1">Interest: {r.lead_info.interest}</p>}
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                r.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' :
                r.status === 'declined' ? 'bg-red-50 text-red-600' :
                'bg-amber-50 text-amber-700'
              }`}>
                {r.status}
              </span>
              {activeView === 'received' && r.status === 'pending' && (
                <div className="flex gap-1.5">
                  <Button size="icon" className="h-8 w-8 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleRespond(r.id, 'accept')} data-testid={`accept-referral-${r.id}`}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg border-red-200 text-red-500 hover:bg-red-50" onClick={() => handleRespond(r.id, 'decline')} data-testid={`decline-referral-${r.id}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Refer Dialog */}
      <Dialog open={showReferDialog} onOpenChange={setShowReferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Refer Lead to {selectedDealer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">Lead Name *</label>
              <Input value={leadForm.name} onChange={e => setLeadForm({...leadForm, name: e.target.value})} className="mt-1 rounded-xl" placeholder="John Doe" data-testid="referral-lead-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input value={leadForm.email} onChange={e => setLeadForm({...leadForm, email: e.target.value})} className="mt-1 rounded-xl" placeholder="john@email.com" data-testid="referral-lead-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <Input value={leadForm.phone} onChange={e => setLeadForm({...leadForm, phone: e.target.value})} className="mt-1 rounded-xl" placeholder="555-1234" data-testid="referral-lead-phone" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Interest / Notes</label>
              <Textarea value={leadForm.interest} onChange={e => setLeadForm({...leadForm, interest: e.target.value})} className="mt-1 rounded-xl" placeholder="Looking for a 2020 Toyota Camry..." rows={3} data-testid="referral-lead-interest" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowReferDialog(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleRefer} disabled={submitting} className="rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 text-white hover:opacity-90" data-testid="submit-referral-btn">
                {submitting ? 'Sending...' : 'Send Referral'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
